/**
 * Supporting-document uploads go to the server in SMALL BATCHES, sequentially, each
 * with a deadline — never one request carrying every file. One request with thirty
 * files converted every file inside it before answering: minutes of "Uploading…",
 * then the proxy dropped it (staging 2026-09-12). Batches keep each request short,
 * give the user visible progress, and let one bad batch fail without losing the rest.
 * Pure planning + an injected uploader — no network here (api.ts owns that).
 */

const MB = 1024 * 1024;

export const UPLOAD_BATCH_MAX_FILES = 5;
export const UPLOAD_BATCH_MAX_BYTES = 30 * MB;
/** Per-batch deadline: generous for a slow link, but finite. */
export const UPLOAD_BATCH_TIMEOUT_MS = 5 * 60 * 1000;

export interface UploadProgress {
  /** Files sent so far (successful or failed). */
  done: number;
  total: number;
}

export interface BatchFailure {
  names: string[];
  reason: string;
}

export interface BatchedUploadResult<T> {
  uploaded: T[];
  failed: BatchFailure[];
}

/** Split files into batches of at most `maxFiles` files and `maxBytes` bytes (a
 *  single file larger than `maxBytes` gets a batch of its own). Order preserved. */
export function planUploadBatches(
  files: readonly File[],
  { maxFiles = UPLOAD_BATCH_MAX_FILES, maxBytes = UPLOAD_BATCH_MAX_BYTES } = {},
): File[][] {
  const batches: File[][] = [];
  let current: File[] = [];
  let bytes = 0;
  for (const f of files) {
    const wouldOverflow =
      current.length > 0 && (current.length >= maxFiles || bytes + f.size > maxBytes);
    if (wouldOverflow) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(f);
    bytes += f.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

/** Upload `files` batch by batch through `send`, reporting progress after each batch.
 *  A failed batch is recorded (its file names + the server's reason) and the loop
 *  continues with the next one, so one bad file never blocks the others. */
export async function uploadInBatches<T>(
  files: readonly File[],
  send: (batch: File[]) => Promise<T[]>,
  onProgress?: (p: UploadProgress) => void,
  onBatchDone?: () => void,
): Promise<BatchedUploadResult<T>> {
  const batches = planUploadBatches(files);
  const uploaded: T[] = [];
  const failed: BatchFailure[] = [];
  let done = 0;
  onProgress?.({ done, total: files.length });
  for (const batch of batches) {
    try {
      uploaded.push(...(await send(batch)));
    } catch (err) {
      failed.push({
        names: batch.map((f) => f.name),
        reason: err instanceof Error ? err.message : 'Upload failed.',
      });
    }
    done += batch.length;
    onProgress?.({ done, total: files.length });
    onBatchDone?.();
  }
  return { uploaded, failed };
}

/** What the user can act on, from a batched result: the failed files grouped by the
 *  reason the server gave, so a name is never shown next to someone else's reason.
 *  Null when nothing failed. */
export function describeBatchFailures(failed: readonly BatchFailure[]): string | null {
  if (failed.length === 0) return null;
  const byReason = new Map<string, string[]>();
  for (const f of failed) {
    byReason.set(f.reason, [...(byReason.get(f.reason) ?? []), ...f.names]);
  }
  const parts = [...byReason.entries()].map(([reason, names]) => {
    const shown = names.slice(0, 3).join(', ') + (names.length > 3 ? ` and ${names.length - 3} more` : '');
    // Reason after an em-dash, not a colon: the reason is a full sentence from the server.
    return `${shown} — ${reason}`;
  });
  // Leads with the FILE, not a count of our failures. "3 documents did not upload" read as
  // something broken on our side and pushed the only actionable part (which file, and why)
  // to the end. The reviewer needs the name first.
  return `Not added: ${parts.join(' · ')}`;
}
